import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Loader2, Download, Trash2, Save, FileText, Upload, Plus } from 'lucide-react';
import { dealerAPI } from '../services/api';
import SuccessScreen from './ui/SuccessScreen';
import { cn } from '../lib/utils';


const ManageDealerBillsModal = ({ productId, isOpen, onClose }) => {
    const [bills, setBills] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Upload State
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadOriginalAmount, setUploadOriginalAmount] = useState('');
    const [uploadPaidAmount, setUploadPaidAmount] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    // Loading & Success states
    const [actionLoading, setActionLoading] = useState({}); // { [billId]: 'save' | 'delete' | 'download' }
    const [isSuccess, setIsSuccess] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [countdown, setCountdown] = useState(3);


    const fetchBills = useCallback(async () => {
        if (!productId) return;
        setIsLoading(true);
        try {
            const response = await dealerAPI.getBills(productId);
            setBills(response.data || []);
        } catch (error) {
            console.error('Error fetching bills:', error);
        } finally {
            setIsLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        if (isOpen && productId) {
            fetchBills();
        }
    }, [isOpen, productId, fetchBills]);

    useEffect(() => {
        let timer;
        if (isSuccess && countdown > 0) {
            timer = setInterval(() => setCountdown(c => c - 1), 1000);
        } else if (isSuccess && countdown === 0) {
            setIsSuccess(false);
            setCountdown(3);
        }
        return () => clearInterval(timer);
    }, [isSuccess, countdown]);

    const handleSuccess = (msg) => {
        setSuccessMsg(msg);
        setIsSuccess(true);
        setCountdown(3);
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            setUploadFile(e.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!uploadFile) return;

        setIsUploading(true);
        try {
            await dealerAPI.uploadBill(productId, uploadFile, uploadOriginalAmount, uploadPaidAmount);
            handleSuccess('Bill uploaded successfully!');
            setUploadFile(null);
            setUploadOriginalAmount('');
            setUploadPaidAmount('');
            fetchBills();
        } catch (error) {
            console.error('Error uploading bill:', error);
        } finally {
            setIsUploading(false);
        }
    };

    const handleUpdateAmount = async (billId, originalAmount, paidAmount) => {
        setActionLoading(prev => ({ ...prev, [billId]: 'save' }));
        try {
            await dealerAPI.updateBill(billId, { originalAmount, paidAmount });
            // handleSuccess('Amounts updated successfully!');
            fetchBills();
        } catch (error) {
            console.error('Error updating bill:', error);
        } finally {
            setActionLoading(prev => ({ ...prev, [billId]: null }));
        }
    };

    const handleDelete = async (billId) => {
        if (!window.confirm('Are you sure you want to delete this bill?')) return;
        setActionLoading(prev => ({ ...prev, [billId]: 'delete' }));
        try {
            await dealerAPI.deleteBill(billId);
            handleSuccess('Bill deleted successfully!');
            fetchBills();
        } catch (error) {
            console.error('Error deleting bill:', error);
        } finally {
            setActionLoading(prev => ({ ...prev, [billId]: null }));
        }
    };

    const handleDownload = async (billId, fileName) => {
        setActionLoading(prev => ({ ...prev, [billId]: 'download' }));
        try {
            const response = await dealerAPI.downloadBill(billId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName || `bill-${billId}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading bill:', error);
        } finally {
            setActionLoading(prev => ({ ...prev, [billId]: null }));
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden flex flex-col p-0 transition-all duration-500 rounded-[3rem] border-0 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950">
                <DialogHeader className="p-8 pb-4 border-b bg-white/40 dark:bg-slate-900/60 dark:border-slate-800 backdrop-blur-3xl sticky top-0 z-40">
                    <div className="flex items-center gap-5">
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-[#FF7D00] to-orange-400 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                            <div className="relative p-3.5 rounded-2xl bg-[#FF7D00]/10 text-[#FF7D00] shadow-sm transform hover:scale-105 transition-transform duration-300">
                                <FileText className="w-8 h-8" />
                            </div>
                        </div>
                        <div>
                            <DialogTitle className="text-3xl font-black tracking-tight text-slate-800 dark:text-white flex items-center gap-2">
                                Vendor Bill Master
                                <span className="text-sm font-black px-3 py-1 bg-[#FF7D00]/10 text-[#FF7D00] rounded-full uppercase tracking-widest ml-1 shadow-sm">Portal</span>
                            </DialogTitle>
                        </div>
                    </div>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto p-8 pt-4 custom-scrollbar">

                {isSuccess ? (
                    <div className="py-10">
                        <SuccessScreen message={successMsg} countdown={countdown} />
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Summary Section */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="p-6 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-inner">
                                <p className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1">Total Liability</p>
                                <p className="text-3xl font-black text-slate-800 dark:text-white tracking-tighter">
                                    ₹{bills.reduce((sum, b) => sum + (b.originalAmount || 0), 0).toLocaleString()}
                                </p>
                            </div>
                            <div className="p-6 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-inner">
                                <p className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1">Paid Amount</p>
                                <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400 tracking-tighter">
                                    ₹{bills.reduce((sum, b) => sum + (b.paidAmount || 0), 0).toLocaleString()}
                                </p>
                            </div>
                            <div className="p-6 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-inner">
                                <p className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1">Pending Due</p>
                                <p className="text-3xl font-black text-rose-600 dark:text-rose-400 tracking-tighter">
                                    ₹{bills.reduce((sum, b) => sum + ((b.originalAmount || 0) - (b.paidAmount || 0)), 0).toLocaleString()}
                                </p>
                            </div>
                        </div>

                        {/* Upload Section */}
                        <div className="p-8 bg-white dark:bg-slate-900/50 rounded-3xl border-2 border-dashed border-slate-200 dark:border-slate-800 shadow-sm transition-all hover:bg-slate-50/50 dark:hover:bg-slate-900/80">
                            <h3 className="text-sm font-black uppercase tracking-widest text-[#FF7D00] mb-6 flex items-center gap-2">
                                <Plus className="w-5 h-5" />
                                Secure New Vendor Bill
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                                <div className="md:col-span-4 space-y-2">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 ml-1">Bill Manifest (PDF/Image)</Label>
                                    <Input type="file" onChange={handleFileSelect} className="jit-input bg-white dark:bg-slate-800/40" />
                                </div>
                                <div className="md:col-span-3 space-y-2">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 ml-1">Original Amount</Label>
                                    <Input 
                                        type="number" 
                                        inputMode="decimal"
                                        placeholder="0.00" 
                                        value={uploadOriginalAmount}
                                        onChange={(e) => setUploadOriginalAmount(e.target.value)}
                                        className="jit-input bg-white dark:bg-slate-800/40" 
                                    />
                                </div>
                                <div className="md:col-span-3 space-y-2">
                                    <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 ml-1">Paid Amount</Label>
                                    <Input 
                                        type="number" 
                                        inputMode="decimal"
                                        placeholder="0.00" 
                                        value={uploadPaidAmount}
                                        onChange={(e) => setUploadPaidAmount(e.target.value)}
                                        className="jit-input bg-white dark:bg-slate-800/40" 
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Button 
                                        onClick={handleUpload} 
                                        disabled={!uploadFile || isUploading}
                                        className="w-full h-12 rounded-xl bg-[#FF7D00] hover:brightness-110 active:scale-95 text-white font-black uppercase tracking-widest shadow-lg shadow-orange-500/20"
                                    >
                                        {isUploading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : <><Upload className="w-5 h-5 mr-2" /> Upload</>}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Bills List */}
                        <div className="border rounded-2xl overflow-hidden bg-white shadow-sm">
                            <Table>
                                <TableHeader className="bg-slate-50">
                                    <TableRow>
                                        <TableHead className="font-bold text-[10px] uppercase">File Name</TableHead>
                                        <TableHead className="font-bold text-[10px] uppercase">Bill Amt</TableHead>
                                        <TableHead className="font-bold text-[10px] uppercase">Paid</TableHead>
                                        <TableHead className="font-bold text-[10px] uppercase">Pending</TableHead>
                                        <TableHead className="font-bold text-[10px] uppercase text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center py-12">
                                                <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-300" />
                                            </TableCell>
                                        </TableRow>
                                    ) : bills.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className="text-center py-12 text-slate-400 font-bold text-xs uppercase italic">
                                                No vendor bills found for this product.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        bills.map((bill) => (
                                            <TableRow key={bill.id}>
                                                <TableCell className="font-bold text-xs max-w-[150px] truncate">
                                                    {bill.fileName}
                                                </TableCell>
                                                <TableCell>
                                                    <Input 
                                                        type="number" 
                                                        className="h-8 w-24 text-xs font-bold"
                                                        value={bill.originalAmount}
                                                        onChange={(e) => {
                                                            const val = parseFloat(e.target.value);
                                                            setBills(prev => prev.map(b => b.id === bill.id ? { ...b, originalAmount: val } : b));
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Input 
                                                        type="number" 
                                                        className="h-8 w-24 text-xs font-bold"
                                                        value={bill.paidAmount}
                                                        onChange={(e) => {
                                                            const val = parseFloat(e.target.value);
                                                            setBills(prev => prev.map(b => b.id === bill.id ? { ...b, paidAmount: val } : b));
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell className="font-bold text-xs text-rose-500 tabular-nums">
                                                    ₹{((bill.originalAmount || 0) - (bill.paidAmount || 0)).toLocaleString()}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <Button 
                                                            variant="ghost" 
                                                            size="icon" 
                                                            className="h-8 w-8 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                                                            onClick={() => handleUpdateAmount(bill.id, bill.originalAmount, bill.paidAmount)}
                                                            disabled={actionLoading[bill.id] === 'save'}
                                                        >
                                                            {actionLoading[bill.id] === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                                        </Button>
                                                        <Button 
                                                            variant="ghost" 
                                                            size="icon" 
                                                            className="h-8 w-8 text-blue-600 hover:bg-blue-50 rounded-lg"
                                                            onClick={() => handleDownload(bill.id, bill.fileName)}
                                                            disabled={actionLoading[bill.id] === 'download'}
                                                        >
                                                            {actionLoading[bill.id] === 'download' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                                                        </Button>
                                                        <Button 
                                                            variant="ghost" 
                                                            size="icon" 
                                                            className="h-8 w-8 text-rose-600 hover:bg-rose-50 rounded-lg"
                                                            onClick={() => handleDelete(bill.id)}
                                                            disabled={actionLoading[bill.id] === 'delete'}
                                                        >
                                                            {actionLoading[bill.id] === 'delete' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                )}

                </div>
                <DialogFooter className="p-6 pt-4 border-t bg-slate-50/50 dark:bg-slate-900/60 dark:border-slate-800 sticky bottom-0 z-30 sm:justify-end items-center gap-4 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.05)]">
                    <Button variant="outline" onClick={onClose} className="h-12 px-8 rounded-xl border-slate-200 dark:border-slate-800 font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest text-[11px]">
                        Terminate Session
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default React.memo(ManageDealerBillsModal);
